import { z } from 'zod';

/**
 * Phase 5 — team chat DTOs.
 *
 * Topic projections only here.  Message + reaction + DM DTOs land alongside
 * their service in subsequent slices to keep the file in one piece per
 * shipped surface.
 */

export const ChatTopicKindSchema = z.enum(['general', 'project', 'custom']);
export type ChatTopicKind = z.infer<typeof ChatTopicKindSchema>;

/** Max length of a channel's last-message preview (W5-0; the list renders "Anna: …"). */
export const CHAT_PREVIEW_MAX = 140;

/**
 * Plain-text excerpt for a list preview: whitespace collapsed, trimmed, cut on
 * a code-point boundary to ≤ `max` UTF-16 units (an ellipsis included). Null
 * for empty text. Used by the API to fill `ChatTopicProjection.lastMessagePreview`.
 */
export function toChatPreview(text: string | null | undefined, max = CHAT_PREVIEW_MAX): string | null {
  const flat = (text ?? '').replace(/\s+/g, ' ').trim();
  if (!flat) return null;
  if (flat.length <= max) return flat;
  let out = '';
  for (const ch of flat) {
    if (out.length + ch.length > max - 1) break;
    out += ch;
  }
  return `${out.trimEnd()}…`;
}

export const ChatTopicSchema = z.object({
  id: z.string().uuid(),
  chatId: z.string().uuid(),
  teamId: z.string().uuid(),
  kind: ChatTopicKindSchema,
  name: z.string(),
  /** Set when kind='project' — the project this topic mirrors. */
  projectId: z.string().uuid().nullable(),
  /** Resolved at projection time so the UI doesn't need a separate fetch. */
  projectSlug: z.string().nullable(),
  projectIcon: z.string().nullable(),
  projectColor: z.string().nullable(),
  isArchived: z.boolean(),
  createdByUserId: z.string().uuid().nullable(),
  lastMessageAt: z.string().datetime().nullable(),
  /**
   * W5-0 · topic-list preview: `toChatPreview(body_text)` of the newest
   * non-deleted message in the channel (thread replies included, like
   * `lastMessageAt`); null when there is none. The web renders
   * "{lastMessageAuthorName}: {lastMessagePreview}". Plain text only — never
   * TipTap JSON. Default null so a server that doesn't send it still parses.
   */
  lastMessagePreview: z.string().max(CHAT_PREVIEW_MAX).nullable().default(null),
  /** Display name (`users.name`) of that message's author; null when there is no message. */
  lastMessageAuthorName: z.string().nullable().default(null),
  createdAt: z.string().datetime(),
});
export type ChatTopicProjection = z.infer<typeof ChatTopicSchema>;

/**
 * Grouped topic list — drives the chat sidebar layout directly.  Sections
 * are pre-sorted by the API (general first, then projects + custom by
 * lastMessageAt desc, archived at the bottom).
 */
export const ChatTopicListSchema = z.object({
  general: ChatTopicSchema.nullable(),
  projects: z.array(ChatTopicSchema),
  custom: z.array(ChatTopicSchema),
  archived: z.array(ChatTopicSchema),
});
export type ChatTopicListProjection = z.infer<typeof ChatTopicListSchema>;

export const CreateChatTopicSchema = z.object({
  name: z.string().trim().min(1).max(60),
});
export type CreateChatTopicInput = z.infer<typeof CreateChatTopicSchema>;

export const UpdateChatTopicSchema = z.object({
  name: z.string().trim().min(1).max(60).optional(),
  description: z.string().max(280).optional(),
});
export type UpdateChatTopicInput = z.infer<typeof UpdateChatTopicSchema>;

// ── Attachments ─────────────────────────────────────────────────────────────

export const ChatAttachmentKindSchema = z.enum(['file', 'image', 'voice', 'task_ref']);
export type ChatAttachmentKind = z.infer<typeof ChatAttachmentKindSchema>;

/** Server-side projection of one attachment row — resolved file/task metadata
 *  inlined so the renderer doesn't fan out per attachment. */
export const ChatAttachmentSchema = z.object({
  id: z.string().uuid(),
  kind: ChatAttachmentKindSchema,
  /** file / image / voice — set when the attachment points at a files row. */
  fileId: z.string().uuid().nullable(),
  fileName: z.string().nullable(),
  fileSizeBytes: z.number().int().nonnegative().nullable(),
  fileMimeType: z.string().nullable(),
  /** task_ref — set when the attachment links a task. */
  taskId: z.string().uuid().nullable(),
  taskKey: z.string().nullable(),
  taskTitle: z.string().nullable(),
  /** Voice messages: total duration in ms.  Null otherwise. */
  durationMs: z.number().int().nonnegative().nullable(),
  /** Voice messages: precomputed waveform peaks (~80 floats in [0,1]). */
  waveform: z.array(z.number().min(0).max(1)).nullable(),
  /** Presigned download URL (15-min TTL).  Used by the renderer to actually
   *  play voice / open files / inline images without a second round-trip
   *  per attachment.  Null for task_ref or when the underlying file row is
   *  inaccessible / soft-deleted. */
  downloadUrl: z.string().nullable().optional(),
});
export type ChatAttachmentProjection = z.infer<typeof ChatAttachmentSchema>;

/** What the composer sends when creating a message.  `fileId` must reference
 *  a file owned by the actor's team that they uploaded; the service validates. */
export const ChatAttachmentInputSchema = z.discriminatedUnion('kind', [
  z.object({
    kind: z.literal('file'),
    fileId: z.string().uuid(),
  }),
  z.object({
    kind: z.literal('image'),
    fileId: z.string().uuid(),
  }),
  z.object({
    kind: z.literal('voice'),
    fileId: z.string().uuid(),
    durationMs: z.number().int().min(100).max(10 * 60 * 1000), // 100ms .. 10 min
    waveform: z.array(z.number().min(0).max(1)).min(8).max(200),
  }),
  z.object({
    kind: z.literal('task_ref'),
    taskId: z.string().uuid(),
  }),
]);
export type ChatAttachmentInput = z.infer<typeof ChatAttachmentInputSchema>;

// ── Reactions ───────────────────────────────────────────────────────────────

export const ChatReactionGroupSchema = z.object({
  emoji: z.string().min(1).max(8),
  count: z.number().int().nonnegative(),
  /** True when the requesting user has reacted with this emoji. */
  mine: z.boolean(),
  /** User ids of everyone who used this emoji on the message — drives tooltips. */
  userIds: z.array(z.string().uuid()),
});
export type ChatReactionGroup = z.infer<typeof ChatReactionGroupSchema>;

export const ToggleChatReactionSchema = z.object({
  emoji: z.string().trim().min(1).max(8),
});
export type ToggleChatReactionInput = z.infer<typeof ToggleChatReactionSchema>;

// ── Messages ────────────────────────────────────────────────────────────────

export const ChatMessageAuthorSchema = z.object({
  id: z.string().uuid(),
  name: z.string(),
  username: z.string(),
  avatarUrl: z.string().nullable(),
});
export type ChatMessageAuthor = z.infer<typeof ChatMessageAuthorSchema>;

export const ChatMessageSchema = z.object({
  id: z.string().uuid(),
  topicId: z.string().uuid(),
  author: ChatMessageAuthorSchema,
  /** TipTap JSON document. */
  body: z.unknown(),
  /** Plaintext mirror — used by FTS, notification previews, and the topic-rail
   *  last-message snippet so the UI doesn't have to re-render TipTap. */
  bodyText: z.string(),
  parentMessageId: z.string().uuid().nullable(),
  attachments: z.array(ChatAttachmentSchema),
  reactions: z.array(ChatReactionGroupSchema),
  /** Count of direct children — drives the "↳ N replies" affordance.
   *  0 when nothing has replied to this message. */
  replyCount: z.number().int().nonnegative(),
  createdAt: z.string().datetime(),
  editedAt: z.string().datetime().nullable(),
  deletedAt: z.string().datetime().nullable(),
});
export type ChatMessageProjection = z.infer<typeof ChatMessageSchema>;

/** Paginated page of messages.  Server orders DESC (newest first) so the
 *  cursor-pagination math is straightforward; client reverses to display
 *  oldest-at-top / newest-at-bottom. */
export const ChatMessagePageSchema = z.object({
  items: z.array(ChatMessageSchema),
  /** Cursor to feed back as ?before=...&beforeId=... for the next page; null
   *  when we've reached the start of the topic. */
  nextCursor: z
    .object({
      before: z.string().datetime(),
      beforeId: z.string().uuid(),
    })
    .nullable(),
});
export type ChatMessagePageProjection = z.infer<typeof ChatMessagePageSchema>;

export const CreateChatMessageSchema = z.object({
  /** TipTap JSON.  The service derives body_text from it. */
  body: z.any(),
  /** Plaintext fallback — required so we never have an empty preview row.
   *  Composers compute it from the TipTap doc and send alongside. */
  bodyText: z.string().min(1).max(20_000),
  parentMessageId: z.string().uuid().nullable().optional(),
  /** @mentions resolved by the composer (mirrors task-comments).  Each id
   *  produces an inbox `chat_mention` for the mentioned user — see
   *  `FanoutService.handleChatMessageCreated`. */
  mentionedUserIds: z.array(z.string().uuid()).max(50).optional(),
  attachments: z.array(ChatAttachmentInputSchema).max(20).optional(),
});
export type CreateChatMessageInput = z.infer<typeof CreateChatMessageSchema>;

export const UpdateChatMessageSchema = z.object({
  body: z.any(),
  bodyText: z.string().min(1).max(20_000),
});
export type UpdateChatMessageInput = z.infer<typeof UpdateChatMessageSchema>;

export const ListChatMessagesQuerySchema = z.object({
  before: z.string().datetime().optional(),
  beforeId: z.string().uuid().optional(),
  /** Children of a specific parent — used by the thread rail. */
  parentMessageId: z.string().uuid().optional(),
  limit: z.coerce.number().int().min(1).max(100).default(50),
});
export type ListChatMessagesQuery = z.infer<typeof ListChatMessagesQuerySchema>;

// ── Reads / unread / mutes ──────────────────────────────────────────────────

export const ChatTopicUnreadSchema = z.object({
  topicId: z.string().uuid(),
  unreadCount: z.number().int().nonnegative(),
  /** True when the user has at least one @mention they haven't seen. */
  hasMention: z.boolean(),
  isMuted: z.boolean(),
  /** My read marker in this channel (null = never read) — the "New messages"
   *  divider on open. Default null so an older server still parses. */
  lastReadAt: z.string().datetime().nullable().default(null),
});
export type ChatTopicUnreadProjection = z.infer<typeof ChatTopicUnreadSchema>;

export const MarkChatTopicReadSchema = z.object({
  /** Message id to mark as the new last-read.  Null = mark whole topic as read. */
  messageId: z.string().uuid().nullable(),
});
export type MarkChatTopicReadInput = z.infer<typeof MarkChatTopicReadSchema>;

export const SetChatTopicMuteSchema = z.object({
  /** Null = unmute.  Date in the future = snooze until then.  Empty = mute forever. */
  mutedUntil: z.string().datetime().nullable().optional(),
});
export type SetChatTopicMuteInput = z.infer<typeof SetChatTopicMuteSchema>;

// ── DMs ─────────────────────────────────────────────────────────────────────

export const DmThreadParticipantSchema = z.object({
  id: z.string().uuid(),
  name: z.string(),
  username: z.string(),
  avatarUrl: z.string().nullable(),
});
export type DmThreadParticipant = z.infer<typeof DmThreadParticipantSchema>;

export const DmThreadSchema = z.object({
  id: z.string().uuid(),
  /** The other participant — never the requesting user.  Drives the rail row. */
  partner: DmThreadParticipantSchema,
  lastMessagePreview: z.string().nullable(),
  lastMessageAt: z.string().datetime().nullable(),
  unreadCount: z.number().int().nonnegative(),
  createdAt: z.string().datetime(),
});
export type DmThreadProjection = z.infer<typeof DmThreadSchema>;

export const DmMessageSchema = z.object({
  id: z.string().uuid(),
  threadId: z.string().uuid(),
  author: ChatMessageAuthorSchema,
  body: z.unknown(),
  bodyText: z.string(),
  attachments: z.array(ChatAttachmentSchema),
  createdAt: z.string().datetime(),
  editedAt: z.string().datetime().nullable(),
  deletedAt: z.string().datetime().nullable(),
});
export type DmMessageProjection = z.infer<typeof DmMessageSchema>;

export const DmMessagePageSchema = z.object({
  items: z.array(DmMessageSchema),
  nextCursor: z
    .object({
      before: z.string().datetime(),
      beforeId: z.string().uuid(),
    })
    .nullable(),
});
export type DmMessagePageProjection = z.infer<typeof DmMessagePageSchema>;

export const OpenDmThreadSchema = z.object({
  /** The user the caller wants to message.  Canonical ordering happens server-side. */
  otherUserId: z.string().uuid(),
});
export type OpenDmThreadInput = z.infer<typeof OpenDmThreadSchema>;

export const CreateDmMessageSchema = z.object({
  body: z.unknown(),
  bodyText: z.string().min(1).max(20_000),
  attachments: z.array(ChatAttachmentInputSchema).max(20).optional(),
});
export type CreateDmMessageInput = z.infer<typeof CreateDmMessageSchema>;

// ── Message requests (Sprint 1.2) ───────────────────────────────────────────

/** Smart "reach out" entry — routes to a direct thread or a pending request
 *  depending on the recipient's who_can_message + shared-team / contact state. */
export const CreateMessageRequestSchema = z.object({
  recipientUserId: z.string().uuid(),
  bodyText: z.string().min(1).max(20_000),
});
export type CreateMessageRequestInput = z.infer<typeof CreateMessageRequestSchema>;

/** Result of a reach-out: either the message was delivered to a real thread,
 *  or it was parked as a request awaiting the recipient's decision. */
export const ReachOutResultSchema = z.discriminatedUnion('kind', [
  z.object({ kind: z.literal('thread'), threadId: z.string().uuid() }),
  z.object({ kind: z.literal('request'), requestId: z.string().uuid() }),
]);
export type ReachOutResult = z.infer<typeof ReachOutResultSchema>;

export const MessageRequestSchema = z.object({
  id: z.string().uuid(),
  sender: DmThreadParticipantSchema,
  firstMessageText: z.string(),
  status: z.enum(['pending', 'accepted', 'declined', 'blocked']),
  createdAt: z.string().datetime(),
});
export type MessageRequestProjection = z.infer<typeof MessageRequestSchema>;

/** Returned by accept — the materialised thread the first message landed in. */
export const AcceptMessageRequestResultSchema = z.object({
  threadId: z.string().uuid(),
});
export type AcceptMessageRequestResult = z.infer<typeof AcceptMessageRequestResultSchema>;
